/**
 * Buffer Tool Dialog
 *
 * Material-UI dialog for configuring buffer analysis parameters.
 * Supports distance input, unit selection, and multiple buffer rings.
 *
 * **Validates: Requirements 22.1, 22.3, 22.5**
 */

import React, { useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '../../store';
import type { DistanceUnits } from '../../services/SpatialAnalysisEngine';
import {
  setBufferDistance,
  setBufferUnits,
  setBufferRings,
  setBufferSourceLayer,
  closeTool,
} from '../../store/slices/spatialAnalysisSlice';

interface BufferToolDialogProps {
  open: boolean;
  onExecute: () => void;
}

const DISTANCE_UNITS: { value: DistanceUnits; label: string }[] = [
  { value: 'meters', label: 'Meters' },
  { value: 'kilometers', label: 'Kilometers' },
  { value: 'feet', label: 'Feet' },
  { value: 'miles', label: 'Miles' },
];

export const BufferToolDialog: React.FC<BufferToolDialogProps> = ({ open, onExecute }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { bufferTool, isProcessing, error, message } = useSelector(
    (state: RootState) => state.spatialAnalysis
  );
  const layers = useSelector((state: RootState) => state.layers.layers);
  const activeLayerId = useSelector((state: RootState) => state.layers.activeLayerId);
  const vectorLayers = layers.filter((l) => l.type === 'vector');

  // Prefill source with active vector layer (or first vector layer)
  useEffect(() => {
    if (!open) return;
    if (bufferTool.sourceLayerId) return;
    const preferred = vectorLayers.find((l) => l.id === activeLayerId) || vectorLayers[0];
    if (preferred) {
      dispatch(setBufferSourceLayer(preferred.id));
    }
  }, [open, bufferTool.sourceLayerId, vectorLayers, activeLayerId, dispatch]);

  const handleClose = useCallback(() => {
    dispatch(closeTool());
  }, [dispatch]);

  const handleDistanceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value);
      if (!isNaN(value)) {
        dispatch(setBufferDistance(value));
      }
    },
    [dispatch]
  );

  const handleUnitsChange = useCallback(
    (e: any) => {
      dispatch(setBufferUnits(e.target.value as DistanceUnits));
    },
    [dispatch]
  );

  const handleRingsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(e.target.value, 10);
      if (!isNaN(value) && value >= 1) {
        dispatch(setBufferRings(value));
      }
    },
    [dispatch]
  );

  const handleSourceLayerChange = useCallback(
    (e: any) => {
      dispatch(setBufferSourceLayer(e.target.value));
    },
    [dispatch]
  );

  const canExecute =
    bufferTool.sourceLayerId && bufferTool.distance > 0 && bufferTool.rings >= 1 && !isProcessing;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Buffer Analysis</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Create buffer polygons around features at a specified distance.
          </Typography>

          <FormControl fullWidth>
            <InputLabel id="buffer-source-layer-label">Source Layer</InputLabel>
            <Select
              labelId="buffer-source-layer-label"
              value={bufferTool.sourceLayerId || ''}
              onChange={handleSourceLayerChange}
              label="Source Layer"
            >
              {vectorLayers.length === 0 ? (
                <MenuItem value="" disabled>
                  No vector layers — import data first
                </MenuItem>
              ) : (
                vectorLayers.map((layer) => (
                  <MenuItem key={layer.id} value={layer.id}>
                    {layer.name}
                    {layer.featureCount != null ? ` (${layer.featureCount})` : ''}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <TextField
            label="Buffer Distance"
            type="number"
            value={bufferTool.distance}
            onChange={handleDistanceChange}
            inputProps={{ min: 0, step: 'any' }}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel id="buffer-units-label">Distance Units</InputLabel>
            <Select
              labelId="buffer-units-label"
              value={bufferTool.units}
              onChange={handleUnitsChange}
              label="Distance Units"
            >
              {DISTANCE_UNITS.map((unit) => (
                <MenuItem key={unit.value} value={unit.value}>
                  {unit.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Number of Rings"
            type="number"
            value={bufferTool.rings}
            onChange={handleRingsChange}
            inputProps={{ min: 1, max: 10, step: 1 }}
            fullWidth
            helperText="Generate concentric buffer rings at equal distance intervals"
          />

          {error && <Alert severity="error">{error}</Alert>}
          {message && !error && <Alert severity="success">{message}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          onClick={onExecute}
          variant="contained"
          disabled={!canExecute}
          startIcon={isProcessing ? <CircularProgress size={16} /> : undefined}
        >
          {isProcessing ? 'Processing...' : 'Create Buffer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BufferToolDialog;
